import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Flex, Box } from 'reflexbox';
import {
  Button,
  Divider,
  Input,
  Text,
  Message,
} from 'rebass';
import Loader from 'react-loading';
import axios from 'axios';
import * as Sentry from '@sentry/browser';

import config from 'frontend/config';
import If from 'frontend/components/If';

class ForgotPasswordPanel extends React.Component {
  static propTypes = {
    onViewLoginClick: PropTypes.func.isRequired,
  };

  state = {
    email: '',

    loading: false,
    error: null,
    success: null,

    failCount: 0,
  };

  resetOnEnter = (e) => {
    if (e.key === 'Enter') {
      this.sendReset();
    }
  };

  sendReset = () => {
    // prevent double click
    if (this.state.loading) {
      return;
    }

    // POST to forgot password API
    this.setState({ loading: true, error: null, success: null });

    axios.post('/api/password/forgot', {
      email: this.state.email,
    }).then(() => {
      this.setState({ loading: false, error: false, success: true, email: '', failCount: 0 });
    }).catch(err => {
      let errorMessage = 'Unknown error occurred';
      if (err.response && err.response.data) {
        errorMessage = err.response.data.message;
      } else {
        Sentry.captureException(err);
      }
      this.setState(prevState => ({
        loading: false,
        error: errorMessage,
        success: false,
        failCount: prevState.failCount + 1,
      }));
    });
  };

  render() {
    const { onViewLoginClick } = this.props;

    let showSafeSenderWarning = false;

    if (this.state.email) {
      const outlookWarningDomains = [
        'hotmail.com',
        'outlook.com',
        'live.com',
      ];
      const match = this.state.email.match(/@(.*)$/);
      if (match) {
        const [, emailDomain] = match;
        showSafeSenderWarning = _.includes(outlookWarningDomains, emailDomain);
      }
    }

    return (
      <Box>
        <If condition={this.state.success}>
          <Message theme="success" p={1}>
            Email has been sent with instructions on how to reset your password.
          </Message>
        </If>
        <If condition={this.state.error}>
          <Message theme="warning" p={1}>
            {this.state.error}
          </Message>
        </If>
        <If
          condition={this.state.failCount >= 3}
          then={() => (
            <Text color={config.colors.red} mb={1} bold>
              Having problems with forgot password? Send an email to
              {' '}
              <a href="mailto:support@raidbots.com?subject=Login Problem">
                support@raidbots.com
              </a>
              {' '}
              and we&apos;ll get back to you as soon as possible.
            </Text>
          )}
        />
        <Input
          label="Email"
          id="forgotEmail"
          name="email"
          type="email"
          maxLength={254}
          value={this.state.email}
          onChange={e => this.setState({ email: e.target.value, error: false, success: false })}
          onKeyPress={this.resetOnEnter}
        />
        <If
          condition={showSafeSenderWarning}
          then={() => (
            <Box mb={2}>
              <Text color={config.colors.orange}>
                Outlook/Hotmail often silently blocks or hides Reset Password emails from Raidbots.
              </Text>
              <Text color={config.colors.orange} mt={1}>
                If the Reset Password email
                does not show up in your inbox within a few minutes,
                add &quot;raidbots.com&quot; to your Safe Senders list (Settings &gt; View all Outlook settings &gt;
                Junk email &gt; Safe senders and domains) and try again.
              </Text>
            </Box>
          )}
        />
        <Flex align="center">
          <Box mr={1}>
            <Button
              id="forgotSubmit"
              theme="success"
              onClick={this.sendReset}
              disabled={this.state.loading}
            >
              Send Reset Email
            </Button>
          </Box>
          <Box>
            {
              this.state.loading
                ? (
                  <Loader
                    type="spin"
                    color={config.colors.green}
                    height={20}
                    width={20}
                    delay={100}
                  />
                ) :
                null
            }
          </Box>
        </Flex>
        <Divider my={1} />
        <Text>
          <a href="#login" onClick={onViewLoginClick}>
            Back to Login
          </a>
        </Text>
      </Box>
    );
  }
}

export default ForgotPasswordPanel;
